import React, { useState, useEffect } from 'react';
import '../styles/ComprehensiveTimesheet.css';

const ComprehensiveTimesheet = ({ initialData, onSave, onCancel }) => {
  const [timesheetData, setTimesheetData] = useState(initialData);
  const [activeSection, setActiveSection] = useState('basic');
  const [totals, setTotals] = useState({
    totalLabour: 0,
    totalEquipment: 0,
    totalConsumables: 0,
    totalPurchases: 0,
    grandTotal: 0
  });

  const days = [
    { key: 'mon', label: 'M' },
    { key: 'tue', label: 'T' },
    { key: 'wed', label: 'W' },
    { key: 'thu', label: 'T' },
    { key: 'fri', label: 'F' },
    { key: 'sat', label: 'S' },
    { key: 'sun', label: 'S' }
  ];

  const labourCodes = [
    { code: 'MW', name: 'Millwright', rate: 85.00 },
    { code: 'MWOT', name: 'Millwright Overtime', rate: 127.50 },
    { code: 'ELE', name: 'Electrician', rate: 85.00 },
    { code: 'ELEOT', name: 'Electrician Overtime', rate: 127.50 }
  ];

  // Calculate totals whenever data changes
  useEffect(() => {
    calculateTotals();
  }, [timesheetData]);

  const calculateTotals = () => {
    // Labour total
    const regularTotal = Object.values(timesheetData.regular_hours).reduce((sum, hours) => sum + Number(hours || 0), 0);
    const overtimeTotal = Object.values(timesheetData.overtime_hours).reduce((sum, hours) => sum + Number(hours || 0), 0);
    const premiumTotal = Object.values(timesheetData.premium_hours).reduce((sum, hours) => sum + Number(hours || 0), 0);
    const travelTotal = Object.values(timesheetData.travel_hours).reduce((sum, hours) => sum + Number(hours || 0), 0);
    
    const totalHours = regularTotal + overtimeTotal + premiumTotal + travelTotal;
    const totalLabour = totalHours * (timesheetData.labour_rate || 0);

    // Equipment total
    const totalEquipment = timesheetData.equipment_used.reduce((sum, item) => sum + Number(item.subtotal || 0), 0);

    // Consumables total
    const totalConsumables = timesheetData.consumables_used.reduce((sum, item) => sum + Number(item.subtotal || 0), 0);

    // Purchases total
    const totalPurchases = timesheetData.purchases.reduce((sum, item) => sum + Number(item.subtotal || 0), 0);

    // Grand total
    const grandTotal = totalLabour + totalEquipment + totalConsumables + totalPurchases;

    setTotals({
      totalLabour,
      totalEquipment,
      totalConsumables,
      totalPurchases,
      grandTotal
    });
  };

  const updateField = (field, value) => {
    setTimesheetData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateHours = (type, day, value) => {
    setTimesheetData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [day]: Number(value) || 0
      }
    }));
  };

  const updateEquipment = (index, field, value) => {
    setTimesheetData(prev => ({
      ...prev,
      equipment_used: prev.equipment_used.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: Number(value) || 0 };
          updatedItem.subtotal = updatedItem.qty * updatedItem.days * updatedItem.rate;
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const updateConsumable = (index, field, value) => {
    setTimesheetData(prev => ({
      ...prev,
      consumables_used: prev.consumables_used.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: Number(value) || 0 };
          if (field === 'qty' || field === 'rate') {
            updatedItem.subtotal = updatedItem.qty * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const updateTool = (tool, value) => {
    setTimesheetData(prev => ({
      ...prev,
      tools_used: {
        ...prev.tools_used,
        [tool]: value
      }
    }));
  };

  const addPurchase = () => {
    setTimesheetData(prev => ({
      ...prev,
      purchases: [...prev.purchases, { name: '', qty: 0, rate: 0, subtotal: 0, po_number: '' }]
    }));
  };

  const updatePurchase = (index, field, value) => {
    setTimesheetData(prev => ({
      ...prev,
      purchases: prev.purchases.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: field === 'name' || field === 'po_number' ? value : Number(value) || 0 };
          if (field === 'qty' || field === 'rate') {
            updatedItem.subtotal = updatedItem.qty * updatedItem.rate;
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const removePurchase = (index) => {
    setTimesheetData(prev => ({
      ...prev,
      purchases: prev.purchases.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = () => {
    onSave(timesheetData);
  };

  const sections = [
    { id: 'basic', label: 'Basic Info', icon: '📋' },
    { id: 'hours', label: 'Hours', icon: '⏰' },
    { id: 'equipment', label: 'Equipment', icon: '🚚' },
    { id: 'consumables', label: 'Consumables', icon: '🔧' },
    { id: 'tools', label: 'Tools', icon: '🛠️' },
    { id: 'purchases', label: 'Purchases', icon: '🛒' },
    { id: 'approval', label: 'Signatures', icon: '✍️' }
  ];

  return (
    <div className="comprehensive-timesheet">
      {/* Mobile Header */}
      <div className="mobile-timesheet-header">
        <h1 className="mobile-header-title">Field Timesheet</h1>
        <button 
          onClick={onCancel}
          className="mobile-close-btn"
          aria-label="Close timesheet"
        >
          ✕
        </button>
      </div>

      {/* Section Navigation */}
      <div className="section-nav">
        {sections.map(section => (
          <button
            key={section.id}
            className={`section-btn ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            <span className="section-icon">{section.icon}</span>
            <span className="section-label">{section.label}</span>
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div className="section-content">
        {activeSection === 'basic' && (
          <div className="basic-info-section">
            <h3>Basic Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Employee Name</label>
                <input
                  type="text"
                  value={timesheetData.employee_name}
                  onChange={(e) => updateField('employee_name', e.target.value)}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label>Week Ending</label>
                <input
                  type="date"
                  value={timesheetData.week_ending}
                  onChange={(e) => updateField('week_ending', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Work Order #</label>
                <input
                  type="text"
                  value={timesheetData.work_order_number}
                  onChange={(e) => updateField('work_order_number', e.target.value)}
                  className="form-input"
                  placeholder="25-1001"
                />
              </div>
              
              <div className="form-group">
                <label>Sub #</label>
                <input
                  type="text"
                  value={timesheetData.sub_number}
                  onChange={(e) => updateField('sub_number', e.target.value)}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Customer Name</label>
              <input
                type="text"
                value={timesheetData.customer_name}
                onChange={(e) => updateField('customer_name', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Job Site</label>
              <input
                type="text"
                value={timesheetData.job_site}
                onChange={(e) => updateField('job_site', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Customer P.O. #</label>
              <input
                type="text"
                value={timesheetData.customer_po_number}
                onChange={(e) => updateField('customer_po_number', e.target.value)}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>Activity Covered by this Report</label>
              <textarea
                value={timesheetData.activity_covered}
                onChange={(e) => updateField('activity_covered', e.target.value)}
                className="form-textarea"
                rows={3}
                placeholder="Describe the work activities performed..."
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Labour Code</label>
                <select
                  value={timesheetData.labour_code}
                  onChange={(e) => {
                    const selectedCode = labourCodes.find(code => code.code === e.target.value);
                    updateField('labour_code', e.target.value);
                    updateField('labour_rate', selectedCode ? selectedCode.rate : 0);
                  }}
                  className="form-select"
                >
                  <option value="">Select Labour Code</option>
                  {labourCodes.map(code => (
                    <option key={code.code} value={code.code}>
                      {code.code} - {code.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'hours' && (
          <div className="hours-section">
            <h3>Daily Hours Tracking</h3>
            
            <div className="hours-table">
              <div className="hours-header">
                <div className="hour-type-label">Hour Type</div>
                {days.map(day => (
                  <div key={day.key} className="day-label">{day.label}</div>
                ))}
                <div className="total-label">Total</div>
              </div>

              {/* Regular Hours */}
              <div className="hours-row">
                <div className="hour-type">Regular</div>
                {days.map(day => (
                  <input
                    key={day.key}
                    type="number"
                    step="0.25"
                    value={timesheetData.regular_hours[day.key]}
                    onChange={(e) => updateHours('regular_hours', day.key, e.target.value)}
                    className="hours-input"
                  />
                ))}
                <div className="total-hours">
                  {Object.values(timesheetData.regular_hours).reduce((sum, hours) => sum + Number(hours || 0), 0)}
                </div>
              </div>

              {/* Overtime Hours */}
              <div className="hours-row">
                <div className="hour-type">Overtime</div>
                {days.map(day => (
                  <input
                    key={day.key}
                    type="number"
                    step="0.25"
                    value={timesheetData.overtime_hours[day.key]}
                    onChange={(e) => updateHours('overtime_hours', day.key, e.target.value)}
                    className="hours-input"
                  />
                ))}
                <div className="total-hours">
                  {Object.values(timesheetData.overtime_hours).reduce((sum, hours) => sum + Number(hours || 0), 0)}
                </div>
              </div>

              {/* Premium Hours */}
              <div className="hours-row">
                <div className="hour-type">Premium</div>
                {days.map(day => (
                  <input
                    key={day.key}
                    type="number"
                    step="0.25"
                    value={timesheetData.premium_hours[day.key]}
                    onChange={(e) => updateHours('premium_hours', day.key, e.target.value)}
                    className="hours-input"
                  />
                ))}
                <div className="total-hours">
                  {Object.values(timesheetData.premium_hours).reduce((sum, hours) => sum + Number(hours || 0), 0)}
                </div>
              </div>

              {/* Travel Hours */}
              <div className="hours-row">
                <div className="hour-type">Travel</div>
                {days.map(day => (
                  <input
                    key={day.key}
                    type="number"
                    step="0.25"
                    value={timesheetData.travel_hours[day.key]}
                    onChange={(e) => updateHours('travel_hours', day.key, e.target.value)}
                    className="hours-input"
                  />
                ))}
                <div className="total-hours">
                  {Object.values(timesheetData.travel_hours).reduce((sum, hours) => sum + Number(hours || 0), 0)}
                </div>
              </div>
            </div>

            <div className="labour-summary">
              <h4>Hours Summary</h4>
              <div className="summary-item">
                <span>Total Hours: </span>
                <span>{Object.values(timesheetData.regular_hours).reduce((sum, hours) => sum + Number(hours || 0), 0) +
                       Object.values(timesheetData.overtime_hours).reduce((sum, hours) => sum + Number(hours || 0), 0) +
                       Object.values(timesheetData.premium_hours).reduce((sum, hours) => sum + Number(hours || 0), 0) +
                       Object.values(timesheetData.travel_hours).reduce((sum, hours) => sum + Number(hours || 0), 0)}</span>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'equipment' && (
          <div className="equipment-section">
            <h3>Equipment & Vehicles</h3>
            
            <div className="equipment-table">
              <div className="equipment-header">
                <div>Equipment</div>
                <div>Qty</div>
                <div>Days</div>
              </div>

              {timesheetData.equipment_used.map((item, index) => (
                <div key={index} className="equipment-row">
                  <div className="equipment-name">{item.name}</div>
                  <input
                    type="number"
                    value={item.qty}
                    onChange={(e) => updateEquipment(index, 'qty', e.target.value)}
                    className="equipment-input small"
                  />
                  <input
                    type="number"
                    step="0.5"
                    value={item.days}
                    onChange={(e) => updateEquipment(index, 'days', e.target.value)}
                    className="equipment-input small"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {activeSection === 'consumables' && (
          <div className="consumables-section">
            <h3>Consumables & Materials</h3>
            
            <div className="consumables-grid">
              {timesheetData.consumables_used.map((item, index) => (
                item.qty > 0 || item.name.includes('Propane') || item.name.includes('7018') || 
                item.name.includes('Zipcut') || item.name.includes('Grinding') ? (
                  <div key={index} className="consumable-item">
                    <div className="consumable-header">
                      <strong>{item.name}</strong>
                    </div>
                    
                    <div className="consumable-inputs">
                      <input
                        type="number"
                        value={item.qty}
                        onChange={(e) => updateConsumable(index, 'qty', e.target.value)}
                        className="qty-input"
                        placeholder="Quantity"
                      />
                    </div>
                  </div>
                ) : null
              ))}
            </div>

            <details className="all-consumables">
              <summary>View All Consumables</summary>
              <div className="consumables-list">
                {timesheetData.consumables_used.map((item, index) => (
                  <div key={index} className="consumable-row">
                    <span className="consumable-name">{item.name}</span>
                    <input
                      type="number"
                      value={item.qty}
                      onChange={(e) => updateConsumable(index, 'qty', e.target.value)}
                      className="consumable-input small"
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </details>

          </div>
        )}

        {activeSection === 'tools' && (
          <div className="tools-section">
            <h3>Tools & Equipment Used</h3>
            
            <div className="tools-checklist">
              <div className="tool-category">
                <h4>Vehicle & Transportation</h4>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.company_vehicle}
                    onChange={(e) => updateTool('company_vehicle', e.target.checked)}
                  />
                  <span>Company Vehicle</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.took_407}
                    onChange={(e) => updateTool('took_407', e.target.checked)}
                  />
                  <span>Took 407 Highway</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.trailer}
                    onChange={(e) => updateTool('trailer', e.target.checked)}
                  />
                  <span>Trailer</span>
                </label>
              </div>

              <div className="tool-category">
                <h4>Welding & Cutting</h4>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.welder}
                    onChange={(e) => updateTool('welder', e.target.checked)}
                  />
                  <span>Welder</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.cutting_torches}
                    onChange={(e) => updateTool('cutting_torches', e.target.checked)}
                  />
                  <span>Cutting Torches</span>
                </label>
              </div>

              <div className="tool-category">
                <h4>Hand Tools & Equipment</h4>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.hammer_drill}
                    onChange={(e) => updateTool('hammer_drill', e.target.checked)}
                  />
                  <span>Hammer Drill</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.roto_hammer}
                    onChange={(e) => updateTool('roto_hammer', e.target.checked)}
                  />
                  <span>Roto Hammer</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.magdrill}
                    onChange={(e) => updateTool('magdrill', e.target.checked)}
                  />
                  <span>Mag Drill</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.auto_level}
                    onChange={(e) => updateTool('auto_level', e.target.checked)}
                  />
                  <span>Auto Level</span>
                </label>
              </div>

              <div className="tool-category">
                <h4>Rigging & Moving Equipment</h4>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.track_jacks}
                    onChange={(e) => updateTool('track_jacks', e.target.checked)}
                  />
                  <span>Track Jacks</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.skates}
                    onChange={(e) => updateTool('skates', e.target.checked)}
                  />
                  <span>Machine Skates</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.dollies}
                    onChange={(e) => updateTool('dollies', e.target.checked)}
                  />
                  <span>Corner Dollies</span>
                </label>
                <label className="tool-item">
                  <input
                    type="checkbox"
                    checked={timesheetData.tools_used.hydraulic_rams}
                    onChange={(e) => updateTool('hydraulic_rams', e.target.checked)}
                  />
                  <span>Hydraulic Rams</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {activeSection === 'purchases' && (
          <div className="purchases-section">
            <h3>Additional Purchases</h3>
            
            <button 
              onClick={addPurchase}
              className="add-purchase-btn"
            >
              + Add Purchase
            </button>

            <div className="purchases-list">
              {timesheetData.purchases.map((purchase, index) => (
                <div key={index} className="purchase-item">
                  <div className="purchase-header">
                    <input
                      type="text"
                      value={purchase.name}
                      onChange={(e) => updatePurchase(index, 'name', e.target.value)}
                      className="purchase-name-input"
                      placeholder="Item description"
                    />
                    <button
                      onClick={() => removePurchase(index)}
                      className="remove-purchase-btn"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="purchase-inputs">
                    <input
                      type="number"
                      value={purchase.qty}
                      onChange={(e) => updatePurchase(index, 'qty', e.target.value)}
                      className="purchase-input"
                      placeholder="Qty"
                    />
                    <input
                      type="text"
                      value={purchase.po_number}
                      onChange={(e) => updatePurchase(index, 'po_number', e.target.value)}
                      className="purchase-input"
                      placeholder="P.O. #"
                    />
                  </div>
                </div>
              ))}
            </div>

          </div>
        )}

        {activeSection === 'approval' && (
          <div className="approval-section">
            <h3>Signatures & Approval</h3>
            
            <div className="signature-section">
              <div className="signature-group">
                <label>Service Technician</label>
                <input
                  type="text"
                  value={timesheetData.technician_signature}
                  onChange={(e) => updateField('technician_signature', e.target.value)}
                  className="signature-input"
                  placeholder="Type name to confirm"
                />
              </div>
              
              <div className="signature-group">
                <label>Customer Approval Name</label>
                <input
                  type="text"
                  value={timesheetData.customer_approval_name}
                  onChange={(e) => updateField('customer_approval_name', e.target.value)}
                  className="signature-input"
                  placeholder="Customer name"
                />
              </div>
              
              <div className="signature-group">
                <label>Customer Signature</label>
                <input
                  type="text"
                  value={timesheetData.customer_signature}
                  onChange={(e) => updateField('customer_signature', e.target.value)}
                  className="signature-input"
                  placeholder="Customer signature"
                />
              </div>
            </div>

            <div className="completion-notice">
              <h4>✓ Timesheet Complete</h4>
              <p>Please ensure all signatures are provided before submitting.</p>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="action-buttons">
        <button onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button 
          onClick={handleSubmit} 
          className="save-btn"
          disabled={!timesheetData.employee_name || !timesheetData.work_order_number}
        >
          Save Timesheet
        </button>
      </div>
    </div>
  );
};

export default ComprehensiveTimesheet;